import React, { useState, useEffect } from 'react';
import { Database, ShieldCheck, Zap, AlertTriangle } from 'lucide-react';

export default function CandorEnterpriseProofSection() {
  const [terminalLines, setTerminalLines] = useState(0);

  useEffect(() => {
    let interval;
    let timeout;
    
    const startAnimation = () => {
      setTerminalLines(0);
      let count = 0;
      interval = setInterval(() => {
        count++;
        setTerminalLines(count);
        if (count >= 4) {
          clearInterval(interval);
          timeout = setTimeout(startAnimation, 4000);
        }
      }, 700);
    };

    startAnimation();

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  return (
    <div className="bg-white py-24 border-t border-gray-100">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-12">
          <div className="inline-block px-3 py-1 mb-6 rounded-full bg-blue-50 text-blue-600 text-[11px] font-bold tracking-[0.15em] uppercase border border-blue-100">
            Catalog-Grounded Enterprise Proof
          </div>
          <h2 className="text-[32px] md:text-[42px] leading-[1.2] font-extrabold text-[#0B1120] mb-8 tracking-tight">
            Schema-Grounded Refactoring for Critical Enterprise Data Warehouses
          </h2>
          
          {/* Alert Box */}
          <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-xl p-5 md:p-6 text-left shadow-sm">
            <h4 className="flex items-center text-[#D97706] font-bold text-[15px] mb-2">
              <AlertTriangle className="w-5 h-5 mr-2 shrink-0" />
              Why Text-Only AI Assistants Break Production Pipelines
            </h4>
            <p className="text-gray-700 text-[14.5px] leading-relaxed">
              Generalist LLM tools operate in a visual text vacuum guessing table structures, hallucinating missing columns, and missing subtle dialect edge cases. Candor runs schema probes against source and target system catalogs <strong>before</strong> refactoring logic, ensuring transformations respect your real data dictionary constraints.
            </p>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Card 1 */}
          <div className="group bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-500/10 hover:border-blue-300 hover:bg-blue-50/30 transition-all duration-300 flex flex-col h-full">
            <div className="flex items-start justify-between mb-6">
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center border border-blue-100 group-hover:bg-blue-600 group-hover:text-white group-hover:scale-110 group-hover:shadow-md transition-all duration-300">
                <Database className="w-6 h-6" />
              </div>
              <div className="px-2.5 py-1 bg-blue-50 text-blue-700 text-[10px] font-bold uppercase tracking-wide rounded-full flex items-center gap-1.5 border border-blue-100 group-hover:bg-blue-100 transition-colors">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></span> Live Catalog
              </div>
            </div>
            
            <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-blue-900 transition-colors">
              1. Simultaneous Schema Inspection
            </h3>
            
            <div className="inline-block px-2.5 py-1 bg-blue-50 text-blue-700 text-[11px] font-bold rounded mb-4 w-max border border-blue-100 group-hover:bg-blue-100 transition-colors">
              No Blind Syntactical Guessing
            </div>
            
            <p className="text-[14px] text-gray-600 leading-relaxed mb-6 flex-grow group-hover:text-gray-800 transition-colors">
              Candor reads system catalog metadata and DDL definitions from both environments to resolve column names, data types, and structural keys.
            </p>
            
            {/* Terminal Block */}
            <div className="bg-[#0B1120] rounded-xl p-4 font-mono text-[11px] leading-[1.6] text-gray-300 w-full overflow-x-auto shadow-inner border border-gray-800 group-hover:border-blue-900/50 transition-colors">
              <div className="flex items-center justify-between text-gray-500 mb-2 border-b border-gray-800 pb-2">
                <div className="flex items-center gap-2">
                  <span className="text-emerald-500 text-[10px]">🟢</span> 
                  <span className="font-semibold text-gray-400 group-hover:text-gray-300 transition-colors">catalog_inspector.sql</span>
                </div>
                <span className="text-[9px] uppercase tracking-wider text-blue-400 font-bold group-hover:text-blue-300 transition-colors">+ LIVE PROBE</span>
              </div>
              <div className={`flex justify-between transition-all duration-300 ${terminalLines >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                <span className="text-amber-500 font-semibold group-hover:text-amber-400 transition-colors">
                  PROBE sys.tables.orders
                  {terminalLines === 1 && <span className="ml-1.5 inline-block w-2 h-3.5 bg-amber-500/80 animate-pulse align-middle"></span>}
                </span>
                <span className="text-gray-400 group-hover:text-gray-300 transition-colors">200 OK</span>
              </div>
              <div className={`flex justify-between mt-1 transition-all duration-300 ${terminalLines >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                <span className="text-amber-500 font-semibold group-hover:text-amber-400 transition-colors">
                  RESOLVE dbo.orders.order_id
                  {terminalLines === 2 && <span className="ml-1.5 inline-block w-2 h-3.5 bg-amber-500/80 animate-pulse align-middle"></span>}
                </span>
                <span className="text-emerald-400 font-bold group-hover:text-emerald-300 transition-colors">INT [PK] ✓</span>
              </div>
              <div className={`flex justify-between mt-1 transition-all duration-300 ${terminalLines >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                <span className="text-amber-500 font-semibold group-hover:text-amber-400 transition-colors">
                  MATCH oracle.plsql.schema
                  {terminalLines === 3 && <span className="ml-1.5 inline-block w-2 h-3.5 bg-amber-500/80 animate-pulse align-middle"></span>}
                </span>
                <span className="text-blue-400 font-bold group-hover:text-blue-300 transition-colors">100% ALIGNED</span>
              </div>
            </div>
          </div>

          {/* Card 2 */}
          <div className="group bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:-translate-y-1 hover:shadow-xl hover:shadow-violet-500/10 hover:border-violet-300 hover:bg-violet-50/30 transition-all duration-300 flex flex-col h-full">
            <div className="flex items-start justify-between mb-6">
              <div className="w-12 h-12 bg-violet-50 text-violet-600 rounded-xl flex items-center justify-center border border-violet-100 group-hover:bg-violet-600 group-hover:text-white group-hover:scale-110 group-hover:shadow-md transition-all duration-300">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div className="px-2.5 py-1 bg-violet-50 text-violet-700 text-[10px] font-bold uppercase tracking-wide rounded-full flex items-center gap-1.5 border border-violet-100 group-hover:bg-violet-100 transition-colors">
                <ShieldCheck className="w-3 h-3" /> Failsafe Guard
              </div>
            </div>
            
            <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-violet-900 transition-colors">
              2. Catalog-Grounded Refactoring
            </h3>
            
            <div className="inline-block px-2.5 py-1 bg-violet-50 text-violet-700 text-[11px] font-bold rounded mb-4 w-max border border-violet-100 group-hover:bg-violet-100 transition-colors">
              Failsafe Metadata Validation
            </div>
            
            <p className="text-[14px] text-gray-600 leading-relaxed mb-6 flex-grow group-hover:text-gray-800 transition-colors">
              If foreign key relationships or schema bounds are ambiguous, Candor flags missing references explicitly rather than inventing columns.
            </p>
            
            {/* Terminal Block */}
            <div className="bg-[#0B1120] rounded-xl p-4 font-mono text-[11px] leading-[1.6] text-gray-300 w-full overflow-x-auto shadow-inner border border-gray-800 group-hover:border-violet-900/50 transition-colors">
              <div className="flex items-center justify-between text-gray-500 mb-2 border-b border-gray-800 pb-2">
                <div className="flex items-center gap-2">
                  <span className="text-emerald-500 text-[10px]">🟢</span> 
                  <span className="font-semibold text-gray-400 group-hover:text-gray-300 transition-colors">schema_validator.sql</span>
                </div>
                <span className="text-[9px] uppercase tracking-wider text-violet-400 font-bold group-hover:text-violet-300 transition-colors">SAFE VALIDATION</span>
              </div>
              <div className={`flex justify-between transition-all duration-300 ${terminalLines >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                <span className="text-blue-400 font-semibold group-hover:text-blue-300 transition-colors">
                  CHECK_FK fk_orders_customer
                  {terminalLines === 1 && <span className="ml-1.5 inline-block w-2 h-3.5 bg-blue-400/80 animate-pulse align-middle"></span>}
                </span>
                <span className="text-emerald-400 font-bold group-hover:text-emerald-300 transition-colors">PASSED ✓</span>
              </div>
              <div className={`flex justify-between mt-1 transition-all duration-300 ${terminalLines >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                <span className="text-blue-400 font-semibold group-hover:text-blue-300 transition-colors">
                  REF_KEY dbo.customers(id)
                  {terminalLines === 2 && <span className="ml-1.5 inline-block w-2 h-3.5 bg-blue-400/80 animate-pulse align-middle"></span>}
                </span>
                <span className="text-emerald-400 font-bold group-hover:text-emerald-300 transition-colors">BOUND ✓</span>
              </div>
              <div className={`flex justify-between mt-1 transition-all duration-300 ${terminalLines >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                <span className="text-red-400 font-semibold group-hover:text-red-300 transition-colors">
                  HALLUCINATED_COLS
                  {terminalLines === 3 && <span className="ml-1.5 inline-block w-2 h-3.5 bg-red-400/80 animate-pulse align-middle"></span>}
                </span>
                <span className="text-emerald-400 font-bold group-hover:text-emerald-300 transition-colors">[ZERO]</span>
              </div>
            </div>
          </div>

          {/* Card 3 */}
          <div className="group bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:-translate-y-1 hover:shadow-xl hover:shadow-emerald-500/10 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all duration-300 flex flex-col h-full relative overflow-hidden">
            <div className="flex items-start justify-between mb-6">
              <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center border border-emerald-100 relative z-10 group-hover:bg-emerald-600 group-hover:text-white group-hover:scale-110 group-hover:shadow-md transition-all duration-300">
                <Zap className="w-6 h-6" />
              </div>
              <div className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wide rounded-full flex items-center gap-1.5 border border-emerald-200 group-hover:bg-emerald-100 transition-colors">
                <Zap className="w-3 h-3 fill-emerald-600 group-hover:fill-emerald-700" /> Auto-Transform
              </div>
            </div>
            
            <h3 className="text-lg font-bold text-gray-900 mb-3 relative z-10 group-hover:text-emerald-900 transition-colors">
              3. Automated Type & Dialect Mapping
            </h3>
            
            <div className="inline-block px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[11px] font-bold rounded mb-4 w-max border border-emerald-100 relative z-10 group-hover:bg-emerald-100 transition-colors">
              Zero Data Type Mismatches
            </div>
            
            <p className="text-[14px] text-gray-600 leading-relaxed mb-6 flex-grow relative z-10 group-hover:text-gray-800 transition-colors">
              Translates T-SQL GETDATE() to SYSDATE, ISNULL() to NVL(), and IDENTITY(1,1) columns to Oracle ALWAYS AS IDENTITY tables automatically.
            </p>
            
            {/* Terminal Block */}
            <div className="bg-[#0B1120] rounded-xl p-4 font-mono text-[11px] leading-[1.6] text-gray-300 w-full overflow-x-auto shadow-inner relative z-10 border border-emerald-900/50 group-hover:border-emerald-700/80 transition-colors">
              <div className="flex items-center justify-between text-gray-500 mb-2 border-b border-gray-800 pb-2">
                <div className="flex items-center gap-2">
                  <span className="text-emerald-500 text-[10px]">🟢</span> 
                  <span className="font-semibold text-gray-400 group-hover:text-gray-300 transition-colors">dialect_transformer.sql</span>
                </div>
                <span className="text-[9px] uppercase tracking-wider text-emerald-400 font-bold group-hover:text-emerald-300 transition-colors">AUTOCONVERT</span>
              </div>
              <div className={`flex justify-between transition-all duration-300 ${terminalLines >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                <span className="text-white font-semibold group-hover:text-gray-100 transition-colors">
                  GETDATE()
                  {terminalLines === 1 && <span className="ml-1.5 inline-block w-2 h-3.5 bg-white/80 animate-pulse align-middle"></span>}
                </span>
                <span className="text-emerald-400 font-bold group-hover:text-emerald-300 transition-colors">SYSDATE</span>
              </div>
              <div className={`flex justify-between mt-1 transition-all duration-300 ${terminalLines >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                <span className="text-white font-semibold group-hover:text-gray-100 transition-colors">
                  ISNULL(status, 'X')
                  {terminalLines === 2 && <span className="ml-1.5 inline-block w-2 h-3.5 bg-white/80 animate-pulse align-middle"></span>}
                </span>
                <span className="text-emerald-400 font-bold group-hover:text-emerald-300 transition-colors">NVL(status, 'X')</span>
              </div>
              <div className={`flex justify-between mt-1 transition-all duration-300 ${terminalLines >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                <span className="text-white font-semibold group-hover:text-gray-100 transition-colors">
                  IDENTITY(1,1)
                  {terminalLines === 3 && <span className="ml-1.5 inline-block w-2 h-3.5 bg-white/80 animate-pulse align-middle"></span>}
                </span>
                <span className="text-emerald-400 font-bold group-hover:text-emerald-300 transition-colors">ALWAYS AS IDENTITY</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
